/**
 * "Can a SOFTWARE keyboard actually appear on this device right now?" — the
 * gate every PREDICTIVE keyboard mechanism must pass before it moves anything.
 *
 * Why this exists (founder desktop capture, 2026-08-23). The keyboard system
 * has two kinds of mechanism:
 *
 *   - REACTIVE ones, keyed on `viewportFrame.keyboardOpen` (glide, curtain,
 *     `useKeyboardFieldReveal`). They can only fire after a real keyboard has
 *     been MEASURED, so they are inert on any device that has none.
 *   - PREDICTIVE ones (`inlineKeyboardGuard`, `useModalKeyboardAnticipation`).
 *     They must act SYNCHRONOUSLY at focusin — before iOS samples the caret —
 *     so they cannot wait for a measurement. They act on a GUESS.
 *
 * A guess needs a gate, and the inline guard shipped without one. On desktop
 * `predictKeyboardTop` falls back to `DEFAULT_KEYBOARD_HEIGHT_PX` (360, which
 * nothing can ever correct there — `recordKeyboardHeight` only fires when a
 * keyboard is actually measured), so a 1144px-tall window predicted a phantom
 * keyboard at y=784 and dodged it: every field whose centre sat below 372px
 * was scrolled up to 350px, with up to 480px of spacer padding injected under
 * it. The founder hit it typing an address into the add-customer drawer — the
 * form jumped and left a large void beneath the last field, on a machine with
 * no on-screen keyboard at all.
 *
 * TWO devices fail the "coarse pointer implies a software keyboard" shortcut,
 * which is why this module is a learned signal and not a one-line media query:
 *
 *   1. Desktop / laptop (fine pointer) — no software keyboard, ever. Decided
 *      statically by `(pointer: coarse)`, no learning needed.
 *   2. iPad (or any tablet) with a HARDWARE keyboard attached — reports
 *      `pointer: coarse` because the screen is still touch, yet focusing a
 *      field raises no keyboard. Indistinguishable from an iPhone up front,
 *      so it can only be learned by OBSERVING that no keyboard arrived.
 *
 * The learning is deliberately asymmetric, because the cost of being wrong is
 * asymmetric: a false "no keyboard" on a real iPhone re-opens the pan lurch
 * that took seven rounds of device captures to close, while a false "keyboard"
 * on an iPad merely wastes a scroll. So:
 *
 *   - It FAILS TOWARD PREDICTING. A coarse pointer predicts by default; only
 *     `MISS_THRESHOLD` consecutive keyboard-less focus sessions suppress it.
 *   - Any single observed software keyboard RESETS the count immediately, so
 *     detaching the iPad's keyboard re-enables the guard on the next focus.
 *   - An iPhone can never accumulate misses (its keyboard always arrives), so
 *     its behavior is byte-identical to before this module existed.
 *
 * Keyed by `innerHeight` for the same reason `keyboardHeightStore` is: it
 * identifies the viewport configuration (orientation, split view), and an
 * iPad's keyboard-attached state travels with a window shape often enough to
 * make per-shape learning better than one global flag.
 */

import { isEditableElement } from '../../hooks/ui/viewportFrame';
import { kbLog } from './keyboardReframeDebug';

const STORAGE_KEY = 'cvx:kb-absent';

/**
 * Consecutive keyboard-less focus sessions before we conclude a hardware
 * keyboard is attached. Three, not one: a user can focus a field and blur it
 * before the keyboard renders, and `classifyProbe` cannot always tell that
 * apart from a genuine absence. Misses persist across launches, so this costs
 * an iPad user three needless reveals ONCE, not three per session.
 */
export const MISS_THRESHOLD = 3;

/**
 * Obscured pixels that mean a real software keyboard — NOT merely iPadOS's
 * shortcuts/accessory bar, which stays on screen with a hardware keyboard
 * attached and reads as roughly 44–55px. `inlineKeyboardGuard`'s own
 * `KEYBOARD_GONE_TOLERANCE_PX` (40) sits BELOW that bar and would classify it
 * as a keyboard, which is exactly the detection this module must not inherit.
 * A real tablet keyboard is 300px+, so 120 separates them with wide margin.
 */
export const SOFTWARE_KEYBOARD_MIN_PX = 120;

/**
 * How long after focusin to check whether a keyboard actually arrived. Device
 * logs put the open at ~100–220ms (two-stage email keyboards finish ~223ms),
 * so 700ms is well past any observed open without lingering into the next
 * interaction.
 */
export const PRESENCE_PROBE_MS = 700;

/* ── pure decisions (no DOM, no storage — exhaustively unit-tested) ───────── */

/**
 * @param {{coarsePointer:boolean, misses:number, threshold?:number}} a
 * @returns {boolean} may a predictive mechanism move the layout?
 */
export function shouldPredictKeyboard({ coarsePointer, misses, threshold = MISS_THRESHOLD }) {
  if (!coarsePointer) return false;
  return !(Number.isFinite(misses) && misses >= threshold);
}

/**
 * Classify one presence probe.
 *
 * `inconclusive` is a first-class outcome and must NOT count as a miss: focus
 * leaving early means the keyboard may have opened and closed unobserved, and
 * a missing `visualViewport` means we cannot measure at all. Counting either
 * would let an iPhone drift toward suppression, which is the one failure this
 * module exists to avoid.
 *
 * @param {{editableStillFocused:boolean, hasVisualViewport:boolean, obscuredPx:number}} a
 * @returns {'hit'|'miss'|'inconclusive'}
 */
export function classifyProbe({ editableStillFocused, hasVisualViewport, obscuredPx }) {
  if (!hasVisualViewport) return 'inconclusive';
  if (obscuredPx >= SOFTWARE_KEYBOARD_MIN_PX) return 'hit';
  if (!editableStillFocused) return 'inconclusive';
  return 'miss';
}

/* ── persisted miss counts ───────────────────────────────────────────────── */

function readMap() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap(map) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* best-effort: private mode / quota must never break focus handling */
  }
}

function viewportKey() {
  return String(Math.round(window.innerHeight));
}

/** Consecutive keyboard-less focus sessions recorded for the current viewport. */
export function readMisses() {
  if (typeof window === 'undefined') return 0;
  const v = parseInt(readMap()[viewportKey()], 10);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

/** Record one keyboard-less focus session for the current viewport. */
export function noteKeyboardAbsent() {
  if (typeof window === 'undefined') return;
  const key = viewportKey();
  const map = readMap();
  const next = readMisses() + 1;
  // Stop counting past the threshold: the decision is already made, and an
  // unbounded integer buys nothing but a larger localStorage value.
  if (next > MISS_THRESHOLD) return;
  map[key] = next;
  writeMap(map);
}

/**
 * A real software keyboard was observed — clear the suppression for this
 * viewport immediately. Called both by the probe and by
 * `useVisualViewportFrame` (whose measurement is the same evidence, arriving
 * sooner), so detaching a tablet keyboard recovers on the very next focus.
 */
export function noteKeyboardObserved() {
  if (typeof window === 'undefined') return;
  const key = viewportKey();
  const map = readMap();
  if (map[key] === undefined) return; // nothing suppressed — avoid a pointless write
  delete map[key];
  writeMap(map);
}

/* ── live predicate ──────────────────────────────────────────────────────── */

/**
 * The learning half: one timer that checks, after a focus has had time to
 * raise a keyboard, whether one actually did.
 *
 * Reads `visualViewport` directly rather than depending on whichever React
 * hooks happen to be mounted — `useVisualViewportFrame` is only enabled for
 * open modals and the bottom bar, so it cannot be the sole observer for a
 * focus on a plain page.
 *
 * @param {Document} doc
 * @returns {{schedule: () => void, cancel: () => void}}
 */
export function createKeyboardPresenceProbe(doc) {
  let timer = 0;
  return {
    schedule() {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const vv = window.visualViewport;
        const obscuredPx = vv ? window.innerHeight - vv.height : 0;
        const verdict = classifyProbe({
          editableStillFocused: isEditableElement(doc.activeElement),
          hasVisualViewport: !!vv,
          obscuredPx,
        });
        if (verdict === 'hit') noteKeyboardObserved();
        else if (verdict === 'miss') noteKeyboardAbsent();
        kbLog('kbPresence', { verdict, obs: Math.round(obscuredPx) });
      }, PRESENCE_PROBE_MS);
    },
    cancel() { clearTimeout(timer); },
  };
}

/** `(pointer: coarse)`, defensively — jsdom and old browsers lack matchMedia. */
export function isCoarsePointer() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return Boolean(window.matchMedia('(pointer: coarse)').matches);
}

/**
 * THE gate. Evaluated at FOCUS time rather than at install/mount time, so
 * device-mode emulation, an external display, or a keyboard being attached
 * mid-session all re-decide on the next focus instead of being latched at boot.
 */
export function canPredictKeyboard() {
  return shouldPredictKeyboard({ coarsePointer: isCoarsePointer(), misses: readMisses() });
}

/**
 * A focus that can actually raise the software keyboard.
 *
 * Trusted `focusin` = the browser focused the control the user touched.
 * Untrusted `.focus()` inside the same turn as `pointerdown` = a click
 * handler focusing the control they just tapped (the qty stepper). Untrusted
 * `.focus()` later — an effect, a rAF, a timeout after adding a row or
 * picking a catalog item — does NOT open the iOS keyboard. Treating it as
 * a coming keyboard lifts the modal, then drops it when no keyboard appears.
 *
 * One document listener, refcounted, so anticipation and the inline guard
 * share the same turn window.
 */
let gesture = false;
let gestureTimer = 0;
let gestureListeners = 0;
function armUserGestureFocus() {
  gesture = true;
  clearTimeout(gestureTimer);
  gestureTimer = setTimeout(() => { gesture = false; }, 0);
}

export function installUserGestureFocusTracking() {
  if (typeof document === 'undefined') return () => {};
  if (gestureListeners === 0) {
    document.addEventListener('pointerdown', armUserGestureFocus, true);
  }
  gestureListeners += 1;
  return () => {
    gestureListeners -= 1;
    if (gestureListeners === 0) {
      document.removeEventListener('pointerdown', armUserGestureFocus, true);
      clearTimeout(gestureTimer);
      gesture = false;
    }
  };
}

export function isUserGestureFocus(event) {
  if (!event) return false;
  if (event.isTrusted) return true;
  return gesture;
}
